import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCreateCustomerContact, useUpdateCustomerContact } from "@/hooks/use-customer-contacts";
import { useSaveLeadContact } from "@/hooks/use-lead-contacts";
import { isValidEmail } from "@/lib/email-validation";
import { normalizePhoneE164 } from "@/lib/phone-format";
import type { DBCustomerContact } from "@/lib/db-types";


interface ContactFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Kunden kontakten hører til; ved lead settes `leadId` i stedet. */
  customerId?: string | null;
  leadId?: string | null;
  /** Satt ved redigering; utelatt = ny kontaktperson. */
  contact?: (Pick<DBCustomerContact, "id" | "full_name" | "email" | "phone"> & { title?: string | null }) | null;
  /** Kalles med den lagrede kontakten, f.eks. så e-post og samtale kan velge den med en gang. */
  onSaved?: (contact: DBCustomerContact) => void;
}

/** Ny eller redigert kontaktperson. Mobil normaliseres til E.164 ved lagring. */
export default function ContactFormDialog({ open, onOpenChange, customerId, leadId, contact, onSaved }: ContactFormDialogProps) {
  const isEdit = !!contact;
  const create = useCreateCustomerContact();
  const update = useUpdateCustomerContact();
  const saveLead = useSaveLeadContact();
  const [fullName, setFullName] = useState("");
  const [title, setTitle] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    if (!open) return;
    setFullName(contact?.full_name ?? "");
    setTitle(contact?.title ?? "");
    setEmail(contact?.email ?? "");
    setPhone(contact?.phone ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact?.id]);

  const emailError = email.trim() && !isValidEmail(email) ? "Ugyldig e-postadresse" : null;
  const phoneError = phone.trim() && !normalizePhoneE164(phone) ? "Ugyldig mobilnummer" : null;
  const busy = create.isPending || update.isPending || saveLead.isPending;
  const canSave = fullName.trim().length > 0 && !emailError && !phoneError && !busy;

  const save = async () => {
    const fields = { full_name: fullName, title, email, phone };
    // Kontakter på leads går via RPC-en, så også møtebookeren kan lagre dem.
    const saved = leadId
      ? await saveLead.mutateAsync({ leadId, contactId: contact?.id ?? null, ...fields })
      : isEdit && contact
        ? await update.mutateAsync({ id: contact.id, ...fields })
        : await create.mutateAsync({ customer_id: customerId!, ...fields });
    onSaved?.(saved);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Rediger kontaktperson" : "Ny kontaktperson"}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) save().catch(() => undefined);
          }}
        >
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">Navn</Label>
              <Input id="contact-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-title">Tittel</Label>
              <Input id="contact-title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email">E-post</Label>
              <Input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={!!emailError}
              />
              {emailError && <p className="text-xs text-destructive">{emailError}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-phone">Mobil</Label>
              <Input
                id="contact-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="912 34 567"
                aria-invalid={!!phoneError}
              />
              {phoneError && <p className="text-xs text-destructive">{phoneError}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" disabled={!canSave} aria-label="Lagre kontaktperson">
              Lagre
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
